import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp as ChartIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useUserSettings } from '../contexts/UserSettingsContext';
import FeatureGuard from '../components/FeatureGuard';

// TradingView Advanced Chart Widget Component
const TradingViewChart: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { settings } = useUserSettings();

  useEffect(() => {
    // Use setTimeout to ensure DOM is ready and avoid React strict mode issues
    const timer = setTimeout(() => {
      if (containerRef.current) {
        // Clear any existing content
        containerRef.current.innerHTML = '';
        
        // Create iframe approach for better isolation
        const iframe = document.createElement('iframe');
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.style.overflow = 'hidden';
        
        // Create the TradingView advanced chart HTML
        const iframeContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body { margin: 0; padding: 0; background: #1e1e1e; height: 100vh; }
              .tradingview-widget-container { width: 100%; height: 100%; }
              .tradingview-widget-container__widget { height: calc(100% - 32px); width: 100%; }
              .tradingview-widget-copyright { 
                font-size: 12px; 
                padding: 8px; 
                text-align: center; 
                background: rgba(0,0,0,0.3); 
                color: rgba(255,255,255,0.7);
                height: 32px;
                box-sizing: border-box;
              }
              .tradingview-widget-copyright a {
                color: #2196F3;
                text-decoration: none;
              }
            </style>
          </head>
          <body>
            <div class="tradingview-widget-container">
              <div class="tradingview-widget-container__widget"></div>
              <div class="tradingview-widget-copyright">
                
              </div>
              <script type="text/javascript" src="https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js" async>
              {
                "autosize": true,
                "symbol": "OANDA:XAUUSD",
                "interval": "60",
                "timezone": "Africa/Johannesburg",
                "theme": "dark",
                "style": "1",
                "locale": "en",
                "hide_side_toolbar": false,
                "allow_symbol_change": true,
                "watchlist": ${JSON.stringify(settings.watchlistSymbols.map(symbol => symbol.proName))},
                "details": true,
                "support_host": "https://www.tradingview.com"
              }
              </script>
            </div>
          </body>
          </html>
        `;
        
        iframe.onload = () => {
          if (iframe.contentDocument) {
            iframe.contentDocument.open();
            iframe.contentDocument.write(iframeContent);
            iframe.contentDocument.close();
          }
        };
        
        containerRef.current.appendChild(iframe);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [settings.watchlistSymbols]);

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: { xs: '500px', md: '700px' },
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#1e1e1e',
        border: '1px solid rgba(255,255,255,0.1)'
      }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

const ChartPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="xl">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography
                variant={isMobile ? 'h4' : 'h2'}
                component="h1"
                gutterBottom
                sx={{ mb: 1 }}
              >
                <ChartIcon sx={{ fontSize: 'inherit', mr: 2, verticalAlign: 'middle' }} />
                Advanced Chart
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: '600px', mx: 'auto' }}
              >
                Professional trading charts with advanced technical analysis tools and real-time market data.
              </Typography>
            </Box>
          </motion.div>

          {/* TradingView Chart */}
          <motion.div variants={itemVariants}>
            <TradingViewChart />
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

function ChartFeature() {
  return (
    <FeatureGuard featureId="chart">
      <ChartPage />
    </FeatureGuard>
  );
}

// Named export for routing - now guarded
export const Chart = ChartFeature;

export default ChartFeature; 